import { makeObservable, observable, runInAction } from 'mobx'

import { SessionConfig } from '../ports'
import { Chat, Contact, MessageReceivedEvent } from '../protocol/types'
import { ChatError, Result, fold, isErr, isOk, mapOk, ok } from '../result'
import { ChatApi } from '../transport/api'
import { ChatSocket } from '../transport/socket'
import { ChatLogStore } from './ChatLog.store'
import { ContactsCache } from './ContactsCache'
import { DraftLogStore, DraftMessage } from './DraftLog.store'
import { InboxStore } from './Inbox.store'

const MAX_CACHED_CHAT_LOGS = 5

// The server answers 409 when the chat already holds the requested archived
// state — the desired outcome is true either way, so it counts as success.
function isAlreadyInRequestedState(result: Result<void, ChatError>): boolean {
  return (
    isErr(result) && result.error.kind === 'http' && result.error.status === 409
  )
}

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected'

export type DirectChatResolution =
  | { readonly kind: 'existing'; readonly chat: Chat }
  | { readonly kind: 'draft' }
  | { readonly kind: 'self' }
  | { readonly kind: 'error'; readonly error: ChatError }

export class ChatSession {
  public connectionStatus: ConnectionStatus = 'connecting'

  public readonly inbox: InboxStore
  public readonly contacts: ContactsCache

  private readonly chatLogs = new Map<string, ChatLogStore>()
  private readonly drafts = new Map<string, DraftLogStore>()
  private activeChannelId: string = null
  private hasConnectedOnce = false
  private isDisposed = false

  public constructor(
    public readonly config: SessionConfig,
    private readonly api: ChatApi,
    private readonly socket: ChatSocket,
  ) {
    this.contacts = new ContactsCache(api, config.clock, config.logger)
    this.inbox = new InboxStore(
      config.userId,
      api,
      this.contacts,
      config.logger,
    )
    makeObservable(this, {
      connectionStatus: observable,
    })
  }

  public start(): void {
    this.socket.start({
      onConnected: this.handleConnected,
      onDisconnected: this.handleDisconnected,
      onMessageReceived: this.handleMessageReceived,
      onChatCreated: this.handleChatCreated,
      onChatUpdated: this.handleChatUpdated,
      onContactJoined: this.inbox.onContactJoined,
      onContactLeft: this.inbox.onContactLeft,
    })

    this.inbox.refresh()
    this.loadOnlineContacts()
  }

  public dispose(): void {
    this.isDisposed = true
    this.socket.stop()
  }

  public openChatLog(chat: Chat): ChatLogStore {
    let log = this.chatLogs.get(chat.id)
    if (!log) {
      // Read confirmation is the single trigger for both the chat2 pill clear
      // and the v1 tab-badge refresh.
      log = new ChatLogStore(
        chat,
        this.config.userId,
        this.api,
        this.contacts,
        this.config.logger,
        this.config.clock,
        this.config.newId,
        () => {
          this.inbox.clearUnread(chat.id)
          this.config.onReadStateChanged?.(chat.id)
        },
      )
      this.chatLogs.set(chat.id, log)
      this.evictStaleChatLogs()
    }

    this.activeChannelId = chat.id
    this.chatLogs.forEach(cached => {
      cached.isActive = cached.chat.id === chat.id
    })
    log.open()
    return log
  }

  public closeChatLog(channelId: string): void {
    const log = this.chatLogs.get(channelId)
    if (log) {
      log.isActive = false
    }
    if (this.activeChannelId === channelId) {
      this.activeChannelId = null
    }
  }

  // Answers "where does messaging this person lead" — an existing
  // conversation (the loaded inbox first, then the authoritative server
  // lookup) or a draft. Navigation stays a screen concern.
  public async resolveDirectChat(
    recipientId: string,
  ): Promise<DirectChatResolution> {
    if (recipientId === this.config.userId) {
      return { kind: 'self' }
    }

    const local = this.inbox.findDirectChat(recipientId)
    if (local) {
      return { kind: 'existing', chat: local }
    }

    const result = await this.api.getDirectChannelId(
      this.config.userId,
      recipientId,
    )
    return fold<string | null, ChatError, DirectChatResolution>(
      result,
      channelId =>
        channelId
          ? {
              kind: 'existing',
              chat: this.knownOrMinimalChat(channelId, recipientId),
            }
          : { kind: 'draft' },
      error => ({ kind: 'error', error }),
    )
  }

  // Conversation commands. The server broadcasts the resulting chat_updated,
  // but its socket fan-out is per-pod and lossy across replicas, so success
  // applies the same transition locally instead of waiting for the echo.
  // Archive state affects the global unread aggregate, hence the read-state
  // callback.
  public async archiveChat(chat: Chat): Promise<Result<void, ChatError>> {
    const result = await this.api.archiveChat(chat.id, this.config.userId)
    const settled = isAlreadyInRequestedState(result) ? ok(undefined) : result
    if (isOk(settled)) {
      this.handleChatUpdated({ ...chat, isArchived: true })
      this.config.onReadStateChanged?.()
    }
    return settled
  }

  public async unarchiveChat(chat: Chat): Promise<Result<void, ChatError>> {
    const result = await this.api.unarchiveChat(chat.id, this.config.userId)
    const settled = isAlreadyInRequestedState(result) ? ok(undefined) : result
    if (isOk(settled)) {
      this.handleChatUpdated({ ...chat, isArchived: false })
      this.config.onReadStateChanged?.()
    }
    return settled
  }

  // Leaving routes a chat without us through the regular update path: the
  // inbox drops it and an open log ejects, same as a server-pushed removal.
  public async leaveChat(chat: Chat): Promise<Result<void, ChatError>> {
    const result = await this.api.leaveChat(chat.id, this.config.userId)
    if (isOk(result)) {
      this.handleChatUpdated({
        ...chat,
        memberIds: chat.memberIds.filter(id => id !== this.config.userId),
      })
      this.config.onReadStateChanged?.()
    }
    return result
  }

  // Group creation mirrors materializeDraft: the server does broadcast
  // chat_created to group members including the creator, but the local
  // insert must not depend on the echo arriving.
  public async createGroup(
    name: string,
    avatarUrl: string,
    memberIds: readonly string[],
  ): Promise<Result<Chat, ChatError>> {
    const allMemberIds = [
      this.config.userId,
      ...memberIds.filter(id => id !== this.config.userId),
    ]
    const result = await this.api.createChat(
      name,
      true,
      this.config.userId,
      allMemberIds,
      avatarUrl,
    )
    return mapOk(result, channelId => {
      const chat: Chat = {
        id: channelId,
        isGroup: true,
        name,
        avatar: avatarUrl || undefined,
        memberIds: allMemberIds,
        createdAt: this.config.clock(),
        createdBy: this.config.userId,
        isArchived: false,
      }
      this.inbox.onChatCreated(chat)
      return chat
    })
  }

  public async updateGroup(chat: Chat): Promise<Result<void, ChatError>> {
    const result = await this.api.updateChat(chat)
    if (isOk(result)) {
      this.handleChatUpdated(chat)
    }
    return result
  }

  public openDraft(recipient: Contact): DraftLogStore {
    let draft = this.drafts.get(recipient.id)
    if (!draft) {
      draft = new DraftLogStore(
        recipient,
        this.config.userId,
        this.api,
        this.config.clock,
        this.config.newId,
        this.config.logger,
        (channelId, pending) =>
          this.materializeDraft(recipient, channelId, pending),
      )
      this.drafts.set(recipient.id, draft)
    }
    return draft
  }

  // The server does not emit chat_created to the creator of a direct chat,
  // so the new conversation is inserted locally from the create response.
  private materializeDraft(
    recipient: Contact,
    channelId: string,
    pending: readonly DraftMessage[],
  ): void {
    const chat: Chat = {
      id: channelId,
      isGroup: false,
      name: recipient.fullName,
      avatar: recipient.avatarUrl,
      memberIds: [this.config.userId, recipient.id],
      createdAt: this.config.clock(),
      createdBy: this.config.userId,
      isArchived: false,
    }

    this.inbox.onChatCreated(chat)
    const log = this.openChatLog(chat)
    log.adopt(pending)

    const draft = this.drafts.get(recipient.id)
    this.drafts.delete(recipient.id)
    draft?.complete(chat)
  }

  // The server knows a channel the loaded inbox does not (stale inbox): use
  // any refreshed copy if one raced in, otherwise a minimal local chat that
  // the next inbox payload trues up.
  private knownOrMinimalChat(channelId: string, recipientId: string): Chat {
    const known = [...this.inbox.chats, ...this.inbox.archivedChats].find(
      chat => chat.id === channelId,
    )
    if (known) {
      return known
    }

    const contact = this.contacts.get(recipientId)
    return {
      id: channelId,
      isGroup: false,
      name: contact?.fullName || '',
      avatar: contact?.avatarUrl,
      memberIds: [this.config.userId, recipientId],
      createdAt: this.config.clock(),
      createdBy: '',
      isArchived: false,
    }
  }

  private evictStaleChatLogs(): void {
    for (const [channelId] of this.chatLogs) {
      if (this.chatLogs.size <= MAX_CACHED_CHAT_LOGS) {
        return
      }
      if (channelId !== this.activeChannelId) {
        this.chatLogs.delete(channelId)
      }
    }
  }

  private handleConnected = () => {
    if (this.isDisposed) {
      return
    }
    runInAction(() => {
      this.connectionStatus = 'connected'
    })

    if (this.hasConnectedOnce) {
      this.catchUpAfterReconnect()
    }
    this.hasConnectedOnce = true
  }

  private handleDisconnected = () => {
    runInAction(() => {
      this.connectionStatus = 'disconnected'
    })
  }

  private catchUpAfterReconnect(): void {
    this.inbox.refresh()
    this.loadOnlineContacts()

    for (const [channelId, log] of [...this.chatLogs]) {
      if (channelId === this.activeChannelId) {
        log.open()
      } else {
        this.chatLogs.delete(channelId)
      }
    }
  }

  private async loadOnlineContacts(): Promise<void> {
    const result = await this.api.getOnlineContacts()
    if (isOk(result) && !this.isDisposed) {
      this.inbox.setOnlineContacts(result.value)
    }
  }

  private handleMessageReceived = (event: MessageReceivedEvent) => {
    this.inbox.onMessageReceived(event)
    this.chatLogs.forEach(log => log.onMessageReceived(event))
  }

  private handleChatCreated = (chat: Chat) => {
    this.inbox.onChatCreated(chat)
  }

  private handleChatUpdated = (chat: Chat) => {
    this.inbox.onChatUpdated(chat)
    this.chatLogs.forEach(log => log.onChatUpdated(chat))
  }
}
